/**
 * ServiceDeskInbox — Freshservice project tickets, promoted into
 * Requested projects. Lived on the Triage page as its "Project tickets"
 * tab until 2026-09-18 (Brett: one list) — now it sits at the top of
 * /intake so a ticket goes ticket → Requested → Approved on one page.
 * Triage itself became /enhancements (the XS routing queue only).
 *
 * Read-only apart from Promote. The list/detail panes, the Unresolved /
 * Resolved / All view, and the BA / bucket / age URL filters carried
 * over unchanged.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Inbox,
  Loader2,
  Paperclip,
  RefreshCw,
  Settings2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  useFreshserviceStatus,
  useFreshservicePreview,
  useFreshserviceImport,
  useFreshserviceTicketDetail,
  type FreshservicePreviewItem,
  type ServiceDeskAttachment,
  type ServiceDeskConversation,
} from "@/hooks/useFreshservice";
import { cn } from "@/lib/cn";
import { attachmentHref } from "@/lib/attachmentHref";
import { BUCKET_META, bucketFor, isHealthBucket } from "@/lib/healthBucket";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PRIORITY_PILL: Record<number, string> = {
  1: "bg-raised text-ink-3",
  2: "bg-raised text-ink-2",
  3: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  4: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
};

/** Render Freshservice-supplied HTML safely. Allows basic formatting tags
 *  (p, br, strong, em, ul/ol/li, headings, links, blockquotes) and strips
 *  scripts, styles, iframes, etc. */
function SanitizedHtml({ html, className }: { html: string; className?: string }) {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "b", "em", "i", "u", "s", "code", "pre",
      "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
      "blockquote", "a", "span", "div", "hr", "table", "thead",
      "tbody", "tr", "td", "th", "img",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title"],
    ADD_ATTR: ["target"],
  });
  return (
    <div
      className={cn("fs-rich-text", className)}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fullDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

type View = "open" | "closed" | "all";

export function ServiceDeskInbox({ group = "primary" }: { group?: "primary" | "ba" }) {
  const navigate = useNavigate();
  const statusQ = useFreshserviceStatus();
  const isConfigured = statusQ.data?.configured === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);

  // View filter — Unresolved / Resolved / All. URL wins (deep links);
  // otherwise localStorage so a manual switch survives a refresh.
  const viewStorageKey = `rp.helpdesk.view.${group}`;
  const viewFromUrl = (() => {
    const v = searchParams.get("view");
    return v === "open" || v === "closed" || v === "all" ? v : null;
  })();
  const [viewState, setViewState] = useState<View>(() => {
    if (viewFromUrl) return viewFromUrl;
    if (typeof window === "undefined") return "open";
    try {
      const v = window.localStorage.getItem(viewStorageKey);
      if (v === "open" || v === "closed" || v === "all") return v;
    } catch { /* fall through */ }
    return "open";
  });
  useEffect(() => {
    if (viewFromUrl && viewFromUrl !== viewState) setViewState(viewFromUrl);
  }, [viewFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = viewState;
  const setViewPersist = (v: View) => {
    setViewState(v);
    try { window.localStorage.setItem(viewStorageKey, v); } catch { /* ignore */ }
    if (searchParams.has("view")) {
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      setSearchParams(next, { replace: true });
    }
  };

  const preview = useFreshservicePreview(isConfigured, view, group);
  const importMut = useFreshserviceImport();
  const isPrimary = group === "primary";

  // Agent / bucket / age filters — URL-driven (BA dashboard deep links).
  const agentParam = searchParams.get("agent");
  const agentIdFilter = useMemo(() => {
    if (!agentParam || agentParam === "unassigned") return null;
    const n = Number(agentParam);
    return Number.isFinite(n) ? n : null;
  }, [agentParam]);
  const filterUnassigned = agentParam === "unassigned";
  const bucketParamRaw = searchParams.get("bucket");
  const bucketFilter = isHealthBucket(bucketParamRaw) ? bucketParamRaw : null;
  const ageParam = searchParams.get("age");
  const ageFilter = useMemo<
    | { kind: "gt"; days: number; label: string }
    | { kind: "lt"; days: number; label: string }
    | { kind: "between"; minDays: number; maxDays: number; label: string }
    | { kind: "since"; sinceMs: number; label: string }
    | null
  >(() => {
    if (ageParam === "gt30") return { kind: "gt", days: 30, label: "Aged > 30d" };
    if (ageParam === "gt90") return { kind: "gt", days: 90, label: "90+ days" };
    if (ageParam === "lt30") return { kind: "lt", days: 30, label: "Last 30 days" };
    if (ageParam === "30to90") return { kind: "between", minDays: 30, maxDays: 90, label: "30–90 days" };
    if (ageParam === "this_week") {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - d.getDay());
      return { kind: "since", sinceMs: d.getTime(), label: "New this week" };
    }
    if (ageParam === "this_month") {
      const now = new Date();
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { kind: "since", sinceMs: d.getTime(), label: "New this month" };
    }
    return null;
  }, [ageParam]);

  const allTickets = preview.data ?? [];
  const tickets = useMemo(() => {
    let list = allTickets;
    // Promoted tickets already live in the planner — the Unresolved
    // view drops them; Resolved/All keep them with their pill.
    if (view === "open") list = list.filter((t) => !t.already_imported);
    if (filterUnassigned) list = list.filter((t) => !t.agent_id);
    else if (agentIdFilter !== null) list = list.filter((t) => t.agent_id === agentIdFilter);
    if (bucketFilter) list = list.filter((t) => bucketFor(t.status_label || "") === bucketFilter);
    if (ageFilter) {
      list = list.filter((t) => {
        if (!t.created_at) return false;
        const t0 = Date.parse(t.created_at);
        if (Number.isNaN(t0)) return false;
        if (ageFilter.kind === "since") return t0 >= ageFilter.sinceMs;
        const age = Math.floor((Date.now() - t0) / 86_400_000);
        if (age < 0) return false;
        if (ageFilter.kind === "gt") return age >= ageFilter.days;
        if (ageFilter.kind === "lt") return age < ageFilter.days;
        if (ageFilter.kind === "between") return age >= ageFilter.minDays && age < ageFilter.maxDays;
        return true;
      });
    }
    return list;
  }, [allTickets, view, agentIdFilter, filterUnassigned, bucketFilter, ageFilter]);

  const filterDisplayName = useMemo(() => {
    if (filterUnassigned) return "Unassigned";
    if (agentIdFilter === null) return null;
    const hit = allTickets.find((t) => t.agent_id === agentIdFilter);
    return hit?.agent_name ?? `BA #${String(agentIdFilter).slice(-5)}`;
  }, [allTickets, agentIdFilter, filterUnassigned]);

  const clearParam = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // Selected ticket — synced with the URL hash so back / link-sharing work.
  const hashId = (() => {
    if (typeof window === "undefined") return null;
    const m = window.location.hash.match(/#(\d+)/);
    return m ? Number(m[1]) : null;
  })();
  const [selectedId, setSelectedId] = useState<number | null>(hashId);
  useEffect(() => {
    setSelectedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, group, agentIdFilter, filterUnassigned, bucketFilter, ageFilter]);
  useEffect(() => {
    if (selectedId !== null) return;
    if (tickets.length === 0) return;
    setSelectedId(tickets[0].ticket_id);
  }, [tickets, selectedId]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (selectedId === null) return;
    const next = `#${selectedId}`;
    if (window.location.hash !== next) window.history.replaceState(null, "", next);
  }, [selectedId]);
  const selected = useMemo(
    () => tickets.find((t) => t.ticket_id === selectedId) ?? null,
    [tickets, selectedId],
  );

  const handlePromote = (ticket: FreshservicePreviewItem) => {
    importMut.mutate([ticket.ticket_id], {
      onSuccess: (resp) => {
        const ok = resp.imported[0];
        const failed = resp.skipped[0];
        if (ok?.project_id) {
          toast.success(`Ticket #${ticket.ticket_id} promoted as ${ok.project_id} — it's in the queue below`);
        } else if (failed?.error) {
          toast.error(`Couldn't promote #${ticket.ticket_id}: ${failed.error}`);
        }
      },
      onError: (err) => toast.error(`Promote failed: ${(err as Error).message}`),
    });
  };

  const promotedHidden = allTickets.filter((t) => t.already_imported).length;

  return (
    <section
      data-testid="service-desk-inbox"
      className="overflow-hidden rounded-xl border border-line bg-surface"
    >
      {/* Header — title, count, and (when connected) the view toggle,
          active filter chips, and Refresh. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-line-soft px-5 py-3">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 text-left"
          aria-expanded={!collapsed}
        >
          <ChevronDown className={cn("h-4 w-4 text-ink-subtle transition-transform", collapsed && "-rotate-90")} />
          <Inbox className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">New from Service Desk</h2>
          {isConfigured && preview.data && (
            <span className="rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-ink-3">
              {tickets.length}
            </span>
          )}
        </button>
        <span className="text-xs text-ink-muted">
          Freshservice project tickets — promote one and it appears in the queue below as Requested.
        </span>
        {isConfigured && !collapsed && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-0.5 rounded-lg border border-line bg-surface p-0.5 text-xs font-medium">
              {([
                { v: "open", label: "Unresolved" },
                { v: "closed", label: "Resolved" },
                { v: "all", label: "All" },
              ] as const).map(({ v, label }) => (
                <button
                  key={v}
                  onClick={() => setViewPersist(v)}
                  className={cn(
                    "rounded-md px-2.5 py-1 transition-colors",
                    view === v ? "bg-slate-900 text-white" : "text-ink-3 hover:bg-raised",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {filterDisplayName && (
              <FilterChip label="BA" value={filterDisplayName} onClear={() => clearParam("agent")} />
            )}
            {bucketFilter && (
              <FilterChip label="Bucket" value={BUCKET_META[bucketFilter].label} onClear={() => clearParam("bucket")} />
            )}
            {ageFilter && (
              <FilterChip label="Age" value={ageFilter.label} onClear={() => clearParam("age")} />
            )}
            <button
              onClick={() => navigate("/settings?tab=integrations")}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-ink-3 transition-colors hover:bg-raised"
              title="Change the Freshservice group / status filter"
            >
              <Settings2 className="h-3.5 w-3.5" /> Filter
            </button>
            <button
              onClick={() => preview.refetch()}
              disabled={preview.isFetching}
              className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", preview.isFetching && "animate-spin")} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="p-4">
          {statusQ.isLoading && (
            <div className="p-8 text-center text-sm text-ink-muted">Loading Service Desk…</div>
          )}

          {!statusQ.isLoading && !isConfigured && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-xs text-amber-800 dark:text-amber-300">
                <span className="font-semibold text-amber-900 dark:text-amber-300">Freshservice isn't connected.</span>{" "}
                Add the <code className="rounded bg-amber-100 dark:bg-amber-500/15 px-1 py-0.5 font-mono text-[10px]">FRESHSERVICE_API_KEY</code>{" "}
                environment variable, then pick the group and status filter in{" "}
                <button onClick={() => navigate("/settings")} className="font-medium underline">Settings</button>.
                Requests submitted through the planner still land in the queue below.
              </div>
            </div>
          )}

          {isConfigured && preview.isLoading && (
            <div className="p-8 text-center text-sm text-ink-muted">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              Loading tickets…
            </div>
          )}

          {isConfigured && preview.isError && (
            <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-800 dark:text-red-300">
              <div className="font-semibold">Couldn't fetch tickets from Freshservice.</div>
              <div className="mt-1 text-red-700 dark:text-red-300">{(preview.error as Error)?.message}</div>
              <div className="mt-2 text-xs text-red-600 dark:text-red-400">If the filter value is wrong, adjust it in Settings → Integrations.</div>
            </div>
          )}

          {isConfigured && preview.data && (
            <div className="grid gap-4 lg:grid-cols-[minmax(340px,420px)_1fr]">
              {/* Left: ticket list */}
              <div className="flex max-h-[520px] flex-col overflow-hidden rounded-lg border border-line">
                <div className="flex items-center justify-between border-b border-line-soft px-4 py-2">
                  <span className="text-xs font-semibold text-ink-2">Tickets</span>
                  {view === "open" ? (
                    promotedHidden > 0 && (
                      <button
                        onClick={() => setViewPersist("all")}
                        className="text-[10px] text-ink-muted hover:text-ink-2 hover:underline"
                        title="Promoted tickets already have an ETE project — they're hidden here. Click to switch to All."
                      >
                        {promotedHidden} promoted · hidden
                      </button>
                    )
                  ) : (
                    <span className="text-[10px] text-ink-muted">
                      {tickets.filter((t) => t.already_imported).length} promoted
                    </span>
                  )}
                </div>
                {tickets.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center py-12 text-center text-ink-muted">
                    <CheckCircle2 className="mb-2 h-7 w-7 text-ink-faint" />
                    <div className="text-sm font-medium">Inbox zero.</div>
                    <div className="mt-0.5 text-xs text-ink-subtle">No tickets match the current filter.</div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto">
                    {tickets.map((t) => (
                      <TicketRow
                        key={t.ticket_id}
                        ticket={t}
                        selected={selectedId === t.ticket_id}
                        onSelect={() => setSelectedId(t.ticket_id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Right: detail */}
              <div className="flex max-h-[520px] flex-col overflow-hidden rounded-lg border border-line">
                {selected ? (
                  <TicketDetail
                    ticket={selected}
                    canPromote={isPrimary}
                    onPromote={() => handlePromote(selected)}
                    promoting={importMut.isPending}
                    onOpenProject={(pid) => navigate(`/portfolio/${pid}`)}
                  />
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center py-12 text-center text-ink-subtle">
                    <Inbox className="mb-2 h-7 w-7 text-ink-faint" />
                    <div className="text-sm font-medium">Select a ticket</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function FilterChip({ label, value, onClear }: { label: string; value: string; onClear: () => void }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-lg border border-brand-navy-200 dark:border-brand-navy-500/30 bg-brand-navy-50 dark:bg-brand-navy-500/10 px-2.5 py-1 text-xs font-medium text-brand-navy-700 dark:text-brand-navy-300">
      <span className="text-[10px] uppercase tracking-wider text-brand-navy-500">{label}</span>
      <span className="font-semibold">{value}</span>
      <button
        onClick={onClear}
        className="ml-1 rounded p-0.5 text-brand-navy-500 hover:bg-brand-navy-100 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
        title={`Clear ${label} filter`}
        aria-label={`Clear ${label} filter`}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ticket list row (left pane)
// ---------------------------------------------------------------------------

function TicketRow({
  ticket,
  selected,
  onSelect,
}: {
  ticket: FreshservicePreviewItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const priorityCls = PRIORITY_PILL[ticket.priority] ?? PRIORITY_PILL[2];
  return (
    <button
      onClick={onSelect}
      className={cn(
        "block w-full border-b border-line-soft px-4 py-3 text-left transition-colors last:border-b-0",
        selected ? "bg-brand-navy-50/70 dark:bg-brand-navy-500/10 ring-1 ring-brand-navy-200 dark:ring-brand-navy-500/30 ring-inset" : "hover:bg-raised",
      )}
    >
      <div className="flex items-start gap-2">
        <span
          className={cn(
            "shrink-0 mt-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
            priorityCls,
          )}
          title={`Priority: ${ticket.priority_label}`}
        >
          {ticket.priority_label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono text-[10px] text-ink-subtle tabular-nums">#{ticket.ticket_id}</span>
            <span className="text-[10px] text-ink-subtle">·</span>
            <span className="text-[10px] text-ink-muted">{shortDate(ticket.created_at)}</span>
          </div>
          <div className="mt-0.5 truncate text-sm font-medium text-ink">{ticket.subject}</div>
          {ticket.requester_name || ticket.requester_email ? (
            <div className="mt-0.5 truncate text-[11px] text-ink-muted">
              {ticket.requester_name || ticket.requester_email}
            </div>
          ) : null}
          {ticket.already_imported && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-2.5 w-2.5" /> Promoted · {ticket.imported_as}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Ticket detail (right pane)
// ---------------------------------------------------------------------------

function TicketDetail({
  ticket,
  canPromote,
  onPromote,
  promoting,
  onOpenProject,
}: {
  ticket: FreshservicePreviewItem;
  /** Only the PMO inbox may promote a ticket into a Requested project.
   *  Application Support tickets feed the enhancement queue instead —
   *  an ungated promote here let the same ticket become both. */
  canPromote: boolean;
  onPromote: () => void;
  promoting: boolean;
  onOpenProject: (projectId: string) => void;
}) {
  const priorityCls = PRIORITY_PILL[ticket.priority] ?? PRIORITY_PILL[2];
  const detailQ = useFreshserviceTicketDetail(ticket.ticket_id);
  const detail = detailQ.data;
  // Prefer detail values when available; fall back to preview data so the pane renders instantly
  const description = detail?.description_text ?? ticket.description_text;
  const descriptionHtml = detail?.description_html ?? null;
  const requesterName = detail?.requester?.name ?? ticket.requester_name ?? null;
  const requesterEmail = detail?.requester?.email ?? ticket.requester_email ?? null;
  const requesterJob = detail?.requester?.job_title ?? null;
  const conversations = detail?.conversations ?? [];

  return (
    <>
      {/* Header */}
      <div className="border-b border-line-soft px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] text-ink-muted">
              <span className="font-mono text-ink-subtle">#{ticket.ticket_id}</span>
              <span>·</span>
              <span>{fullDate(ticket.created_at)}</span>
            </div>
            <h2 className="mt-1 text-base font-semibold text-ink leading-snug">
              {ticket.subject}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", priorityCls)}>
                {ticket.priority_label}
              </span>
              <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-3">
                {ticket.status_label}
              </span>
              {ticket.already_imported && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-2.5 w-2.5" /> Promoted · {ticket.imported_as}
                </span>
              )}
            </div>
          </div>
          <a
            href={ticket.url}
            target="_blank"
            rel="noreferrer"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
            title="Open in Freshservice"
          >
            <ExternalLink className="h-3 w-3" /> Freshservice
          </a>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-5">
          {/* Attachments — ticket-level (prominent, near top) */}
          {detail && detail.attachments.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle mb-2 flex items-center gap-1.5">
                <Paperclip className="h-3 w-3" />
                {detail.attachments.length} {detail.attachments.length === 1 ? "Attachment" : "Attachments"}
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.attachments.map((a) => (
                  <AttachmentChip key={a.id} attachment={a} />
                ))}
              </div>
            </div>
          )}

          {/* Requester */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle mb-1">
              Requester
            </div>
            {requesterName || requesterEmail ? (
              <div className="text-sm text-ink-2">
                {requesterName && (
                  <div className="font-medium">
                    {requesterName}
                    {requesterJob && (
                      <span className="ml-2 text-xs font-normal text-ink-muted">{requesterJob}</span>
                    )}
                  </div>
                )}
                {requesterEmail && (
                  <div className="text-xs text-ink-muted">
                    <a
                      href={`mailto:${requesterEmail}`}
                      className="hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors"
                    >
                      {requesterEmail}
                    </a>
                  </div>
                )}
              </div>
            ) : detailQ.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-ink-subtle">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading requester…
              </div>
            ) : (
              <div className="text-xs text-ink-subtle italic">
                No requester info available on this ticket.
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                Description
              </div>
              {detailQ.isLoading && (
                <div className="flex items-center gap-1 text-[10px] text-ink-subtle">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" /> Loading full details…
                </div>
              )}
              {detailQ.isError && (
                <span className="text-[10px] text-amber-700 dark:text-amber-300">Couldn't load full details — showing preview</span>
              )}
            </div>
            {descriptionHtml ? (
              <SanitizedHtml
                html={descriptionHtml}
                className="rounded-lg border border-line bg-canvas/60 p-3 text-sm text-ink-2 leading-relaxed"
              />
            ) : description ? (
              <div className="whitespace-pre-wrap rounded-lg border border-line bg-canvas/60 p-3 text-sm text-ink-2 leading-relaxed">
                {description}
              </div>
            ) : (
              <div className="text-xs text-ink-subtle italic">No description provided.</div>
            )}
          </div>

          {/* Conversations */}
          {conversations.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle mb-2">
                Conversation ({conversations.length})
              </div>
              <div className="space-y-2">
                {conversations.map((c) => (
                  <ConversationCard key={c.id} convo={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action bar */}
      <div className="border-t border-line-soft bg-canvas/60 px-6 py-3">
        {ticket.already_imported && ticket.imported_as ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-ink-3">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span>
                Already in Portfolio as{" "}
                <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-300">{ticket.imported_as}</span>
              </span>
            </div>
            <button
              onClick={() => onOpenProject(ticket.imported_as!)}
              className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 transition-colors"
            >
              Open project <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        ) : canPromote ? (
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-ink-muted">
              Promoting creates a{" "}
              <span className="rounded-full bg-violet-100 dark:bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                Requested
              </span>{" "}
              project in the Portfolio.
            </div>
            <button
              onClick={onPromote}
              disabled={promoting}
              className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-4 py-2 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
            >
              {promoting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Promote to Requested
            </button>
          </div>
        ) : (
          <div className="text-[11px] text-ink-muted">
            Small Enhancement requests (Request Type in the Project Management
            group) go to the{" "}
            <Link
              to="/top-projects?view=enhancements"
              className="font-medium text-brand-navy-600 dark:text-brand-navy-400 hover:underline"
            >
              Small Enhancements
            </Link>{" "}
            list on Top Projects — approve and route them there, not here.
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Conversation card (reply / note)
// ---------------------------------------------------------------------------

function ConversationCard({ convo }: { convo: ServiceDeskConversation }) {
  const isNote = convo.is_private;
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5",
        isNote
          ? "border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10"
          : "border-line bg-surface",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-ink">
            {convo.author_name || "Unknown"}
          </span>
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider",
              isNote
                ? "bg-amber-200 text-amber-900 dark:text-amber-300"
                : "bg-muted text-ink-2",
            )}
          >
            {convo.source_label}
          </span>
        </div>
        <span className="text-[10px] text-ink-subtle tabular-nums">
          {shortDate(convo.created_at)}
        </span>
      </div>
      {convo.body_html ? (
        <SanitizedHtml
          html={convo.body_html}
          className="mt-1.5 text-sm text-ink-2 leading-relaxed"
        />
      ) : convo.body_text ? (
        <div className="mt-1.5 whitespace-pre-wrap text-sm text-ink-2 leading-relaxed">
          {convo.body_text}
        </div>
      ) : (
        <div className="mt-1.5 text-xs text-ink-subtle italic">(empty)</div>
      )}
      {convo.attachments && convo.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {convo.attachments.map((a) => (
            <AttachmentChip key={a.id} attachment={a} compact />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachment chip (image thumbnail OR file pill — opens in new tab)
// ---------------------------------------------------------------------------

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const keep = max - 1;
  const left = Math.ceil(keep * 0.6);
  const right = keep - left;
  return s.slice(0, left) + "…" + s.slice(s.length - right);
}

function AttachmentChip({
  attachment,
  compact = false,
}: {
  attachment: ServiceDeskAttachment;
  compact?: boolean;
}) {
  const sizeLabel = formatBytes(attachment.size);
  const displayName = truncateMiddle(attachment.name, 36);

  // Image: show small thumbnail. Click opens full-size in new tab.
  if (attachment.is_image && attachment.url) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className={cn(
          "group inline-flex flex-col items-stretch overflow-hidden rounded-md border border-line bg-surface hover:border-brand-navy-300 hover:shadow-sm transition-all",
          compact ? "max-w-[140px]" : "max-w-[180px]",
        )}
        title={`${attachment.name}${sizeLabel ? ` · ${sizeLabel}` : ""}`}
      >
        <div className={cn("bg-raised overflow-hidden", compact ? "h-20" : "h-24")}>
          <img
            src={attachment.url}
            alt={attachment.name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        </div>
        <div className="flex items-center gap-1 px-2 py-1 text-[10px] text-ink-3">
          <ImageIcon className="h-2.5 w-2.5 shrink-0 text-ink-subtle" />
          <span className="truncate">{displayName}</span>
          {sizeLabel && <span className="ml-auto text-ink-subtle tabular-nums">{sizeLabel}</span>}
        </div>
      </a>
    );
  }

  // Non-image: file pill with icon + name + size.
  // attachmentHref() wraps Office types in the Office Online viewer so
  // .docx/.xlsx/.pptx render in-browser rather than downloading —
  // matching what the Resources card already does server-side.
  return (
    <a
      href={attachmentHref(attachment.url, {
        filename: attachment.name,
        contentType: attachment.content_type,
      })}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "inline-flex items-center gap-2 rounded-md border border-line bg-surface hover:border-brand-navy-300 hover:bg-brand-navy-50/40 dark:hover:bg-brand-navy-500/10 transition-colors",
        compact ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs",
      )}
      title={`${attachment.name}${sizeLabel ? ` · ${sizeLabel}` : ""}${attachment.content_type ? ` · ${attachment.content_type}` : ""}`}
    >
      <FileText className={cn("shrink-0 text-brand-navy-500", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
      <span className="font-medium text-ink-2 truncate">{displayName}</span>
      {sizeLabel && (
        <span className="text-ink-subtle tabular-nums">{sizeLabel}</span>
      )}
    </a>
  );
}
